import moment from "moment";
import {
  isInLastNBusinessDay,
  isTodayBetween25AndEndOfMonth,
} from "@/helpers/utils";
import {
  subscriptionPaymentStatus,
  subscriptionPlanPaymentType,
} from "@/config/constants";

type PackageInfo = {
  amount?: number | string | null;
  brand_match_amount?: number | string | null;
  cad_amount?: number | string | null;
  cad_extra_charge?: number | string | null;
  currency?: number | string | null;
  extra_charge?: number | string | null;
  payment_type?: string;
  period?: number | string;
};

type SubscriptionSelection = {
  packageInfo?: PackageInfo;
};

type LocationInfo = {
  isDelayed?: boolean;
  is_current_subscription_active?: boolean;
  payment_status?: string;
  stripe_subscription_id?: string | null;
  subscription_end_date?: string | null;
  upcoming_package_duration?: number | string;
};

export const subscriptionHelper = {
  calculateSubscriptionTotal,
  calculateAllSubscriptionTotals,
  calculateMediaTotal,
  hasUsdCadCurrency,
  getPackageFeeAmount,
  getPackageMediaAmount,
  isRenewing,
  isDelayedRenewing,
  calculateSubscriptionDate,
  isAutoRenewDateGone,
  totalBrandMatchAmount,
  calculateMediaAndSubscriptionTotal,
  checkIsNewSubscription,
};

function totalBrandMatchAmount(selections: SubscriptionSelection[] = []) {
  return selections.reduce(
    (t, v) => t + Number(v.packageInfo?.brand_match_amount),
    0
  );
}

function getPackageAmount(
  packageInfo: PackageInfo = {},
  field: "amount" | "extra_charge",
  useCadAmount = false
) {
  const cadField = field === "amount" ? "cad_amount" : "cad_extra_charge";
  const cadAmount = packageInfo?.[cadField];
  return useCadAmount && cadAmount !== undefined && cadAmount !== null
    ? cadAmount
    : packageInfo?.[field];
}

function getPackageMediaAmount(
  packageInfo: PackageInfo = {},
  useCadAmount = false
) {
  return Number(getPackageAmount(packageInfo, "amount", useCadAmount));
}

function getPackageFeeAmount(
  packageInfo: PackageInfo = {},
  useCadAmount = false
) {
  return Number(getPackageAmount(packageInfo, "extra_charge", useCadAmount));
}

function hasUsdCadCurrency(packageInfo: PackageInfo = {}) {
  const currency = String(packageInfo?.currency || "").toUpperCase();
  return currency.includes("USD") && currency.includes("CAD");
}

function calculateMediaAndSubscriptionTotal(
  selections: SubscriptionSelection[] = [],
  useCadAmount = false
) {
  return selections.reduce(
    (t, v) =>
      t +
      getPackageMediaAmount(v.packageInfo, useCadAmount) +
      getPackageFeeAmount(v.packageInfo, useCadAmount),
    0
  );
}

function calculateMediaTotal(
  selections: SubscriptionSelection[] = [],
  useCadAmount = false
) {
  return selections.reduce(
    (t, v) => t + getPackageMediaAmount(v.packageInfo, useCadAmount),
    0
  );
}

function calculateSubscriptionTotal(
  selections: SubscriptionSelection[] = [],
  useCadAmount = false
) {
  return selections.reduce(
    (t, v) => t + getPackageFeeAmount(v.packageInfo, useCadAmount),
    0
  );
}

function calculateAllSubscriptionTotals(
  selections: SubscriptionSelection[] = [],
  useCadAmount = false
) {
  const totalMediaAmount = calculateMediaTotal(selections, useCadAmount);

  const totalSubscriptingFee = selections.reduce(
    (t, v) => t + getPackageFeeAmount(v.packageInfo, useCadAmount),
    0
  );

  const total = totalMediaAmount + totalSubscriptingFee;
  return { total, totalSubscriptingFee, totalMediaAmount };
}

/**
 * @method isRenewing
 * @description checking is renewing the location
 * @param {*} locationInfo
 * @returns
 */
function isRenewing(locationInfo: LocationInfo = {}) {
  const { is_current_subscription_active } = locationInfo;

  if (!is_current_subscription_active) return false;

  // if (moment(subscription_end_date).diff(moment(), "days") > 90) {
  //   return false;
  // }
  return true;
}

/**
 * @method isDelayedRenewing
 * @description check is renewing after previous subscription already ended
 * @param {*} locationInfo
 * @returns
 */
function isDelayedRenewing(locationInfo: LocationInfo = {}) {
  const { is_current_subscription_active, subscription_end_date } =
    locationInfo;

  if (!is_current_subscription_active && subscription_end_date) return true;

  return false;
}

/**
 * @method calculateSubscriptionDate
 * @description calculate & return subscription date
 * @param {*} packageInfo
 * @param {*} locationInfo
 * @returns
 */
function calculateSubscriptionDate(
  packageInfo: PackageInfo,
  locationInfo: LocationInfo,
  isChangingUpcomingPackage?: boolean
) {
  const { period, payment_type } = packageInfo;

  const {
    is_current_subscription_active,
    subscription_end_date,
    isDelayed,
    upcoming_package_duration,
  } = locationInfo;

  const isNewSubscription = checkIsNewSubscription(locationInfo);

  const today = moment();

  const isTodayBetween25thAndEndOfMonth = isTodayBetween25AndEndOfMonth(
    moment(today.format("YYYY-MM-DD"))
  );

  const isTodayInLast4BusinessDays = isInLastNBusinessDay(
    moment(today.format("YYYY-MM-DD")),
    4
  );

  let startDate = moment(today).add(1, "month").startOf("month");

  if (is_current_subscription_active && subscription_end_date) {
    // if current subscription is active
    startDate = moment(subscription_end_date).add(1, "month").startOf("month");
    if (
      isTodayBetween25thAndEndOfMonth &&
      payment_type === subscriptionPlanPaymentType.STRIPE
    ) {
      /* When current subscription is active, user opt-out before 24, then opt-in on 26, if selected stripe plan while opt-in, or change then need to give next to next month start date. 
      https://hyperlocology.atlassian.net/browse/HYPEPROD-4963?focusedCommentId=15308
      */
      startDate = moment(today).add(2, "months").startOf("month");
    }
  } else if (!isNewSubscription && !isDelayed && subscription_end_date) {
    // when not new subscription and not delayed
    // previously subscribed but no current active subscription
    // treat it as default, no 5 day buffer
    startDate = moment(subscription_end_date).add(1, "month").startOf("month");
  } else if (
    payment_type === subscriptionPlanPaymentType.INVOICE
      ? isTodayInLast4BusinessDays
      : isTodayBetween25thAndEndOfMonth
  ) {
    // For new Subscribers, If today is between the 25th and end of the month then, start date will be 1st of next to next month

    startDate = moment(today).add(2, "months").startOf("month");
  }

  let endDate = moment(today).add(1, "month").startOf("month");
  // if (isNewSubscription) {
  //   startDate = moment(today).add(1, "month").startOf("month");
  // }
  if (isChangingUpcomingPackage) {
    endDate = moment(startDate)
      .add(parseInt(String(upcoming_package_duration)), "month")
      .subtract(1, "day");
  } else {
    endDate = moment(startDate)
      .add(parseInt(String(period)), "month")
      .subtract(1, "day");
  }

  return { startDate, endDate };
}

/**
 * @method isAutoRenewDateGone
 * @description return is auto renewal date which the 1st of subscription end month has gone
 * @param {*} location
 * @returns
 */
function isAutoRenewDateGone(location: LocationInfo) {
  const { subscription_end_date } = location;
  const currentSubEndDate = moment(subscription_end_date);
  if (!currentSubEndDate.isValid()) {
    return false;
  }

  const firstDayOfEndMonth = currentSubEndDate.startOf("month");

  return moment().isAfter(firstDayOfEndMonth, "day");
}

/**
 * @method checkIsNewSubscription
 * @description Only for stripe, Check if location is new subscription, by checking if payment status is UNPAID and stripe_subscription_id exists
 * @param {Object} location
 * @returns {Boolean}
 */
function checkIsNewSubscription(location: LocationInfo) {
  const {
    payment_status,
    stripe_subscription_id,
    is_current_subscription_active,
  } = location;
  return !is_current_subscription_active &&
    (payment_status === subscriptionPaymentStatus.UNPAID ||
      payment_status === subscriptionPaymentStatus.PENDING) &&
    stripe_subscription_id
    ? true
    : false;
}
